import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Button,
  ConfigProvider,
  Dropdown,
  Empty,
  Input,
  Menu,
  Modal,
  Pagination,
  Spin,
  Table,
  Tag,
  message,
} from "antd";
import {
  PlusOutlined,
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";
import { MakeApiCall } from "../../../apis";
import { useThemeMode } from "../../../contexts/ThemeContext";
import { getLocalAntdTheme } from "../../../theme/antdTheme";
import { Wrapper } from "./style";
import InviteSubUserForm from "./lib/InviteSubUserForm";
import {
  ROLE_LABEL_MAP,
  SUB_USER_STATUS_LABELS,
  normalizeSubUserRecord,
} from "./lib/constants";
import { useCanEdit } from "../../../contexts/SubUserAccessContext";

const roleTagClass = (role) => {
  if (role === "account_admin") return "mu-role-tag mu-role-tag--admin";
  if (role === "manager") return "mu-role-tag mu-role-tag--manager";
  return "mu-role-tag mu-role-tag--analyst";
};

export default function ManageUsers({ userData }) {
  const { themeMode } = useThemeMode();
  const theme = getLocalAntdTheme(themeMode, "#f45b68");
  const canEdit = useCanEdit();

  const [view, setView] = useState("list");
  const [editingUser, setEditingUser] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPage, setTotalPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Add isMobile state to detect if user is on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchSubUsers = async (opts = {}) => {
    setLoading(true);
    const currentPage = opts.page ?? page;
    const currentSize = opts.pageSize ?? pageSize;
    const currentSearch = opts.searchText ?? searchText;
    const url = `sub-users?search_text=${encodeURIComponent(currentSearch)}&page=${currentPage}&per-page=${currentSize}`;

    try {
      const response = await MakeApiCall(url, "get", null, true, {}, userData?.token);
      if (response?.status) {
        setTotalPage(response?.data?.pagination?.totalCount || 0);
        setList((response?.data?.records || []).map(normalizeSubUserRecord));
      } else {
        setList([]);
        setTotalPage(0);
      }
    } catch {
      setList([]);
      setTotalPage(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (view === "list") fetchSubUsers();
  }, [page, pageSize, searchText, view]);

  const onSearch = debounce((value) => {
    setSearchText(value);
    setPage(1);
  }, 400);

  const handleDelete = async (id) => {
    message.loading("Deleting…", 0);
    const response = await MakeApiCall(`sub-users/${id}`, "delete", null, true, {}, userData?.token);
    message.destroy();
    if (response?.status) {
      message.success(response?.message || "Sub-user removed.");
      setDeleteTarget(null);
      fetchSubUsers();
    } else {
      message.warning(response?.message || "Failed to delete sub-user.");
    }
  };

  const handleResendInvite = async (record) => {
    message.loading("Resending invite…", 0);
    const response = await MakeApiCall(
      `sub-users/${record.id}/resend-invite`,
      "post",
      null,
      true,
      {},
      userData?.token
    );
    message.destroy();
    if (response?.status) {
      message.success(response?.message || "Invite resent.");
    } else {
      message.warning(response?.message || "Could not resend invite.");
    }
  };

  const openInvite = () => {
    if (!canEdit) return;
    setEditingUser(null);
    setView("invite");
  };

  const openEdit = (record) => {
    if (!canEdit) return;
    setEditingUser(record);
    setView("invite");
  };

  const backToList = () => {
    setEditingUser(null);
    setView("list");
  };

  useEffect(() => {
    if (view === "invite" && !canEdit) {
      setEditingUser(null);
      setView("list");
    }
  }, [view, canEdit]);

  const handleInviteSuccess = () => {
    setEditingUser(null);
    setView("list");
    setPage(1);
    fetchSubUsers({ page: 1 });
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setPage(1);
  };


  const actionMenu = (record) => (
    <Menu className="min-w-200px p-2">
      <Menu.Item key="edit" className="d-flex align-items-center" onClick={() => openEdit(record)}>
        <EditOutlined className="me-2" /> Edit User
      </Menu.Item>
      {record.status === "pending" ? (
        <Menu.Item key="resend" className="d-flex align-items-center" onClick={() => handleResendInvite(record)}>
          <MailOutlined className="me-2" /> Resend invite
        </Menu.Item>
      ) : null}
      <Menu.Divider />
      <Menu.Item key="delete" className="d-flex align-items-center" onClick={() => setDeleteTarget(record)}>
        <DeleteOutlined className="me-2 text-danger" />
        <span className="text-danger">Remove user</span>
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "#",
      key: "index",
      width: 48,
      render: (_, __, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Team member",
      key: "member",
      render: (_, record) => {
        const initials = record.name
          ?.split(/\s+/)
          .map((w) => w[0])
          .join("")
          .slice(0, 2)
          .toUpperCase() || "?";
        return (
          <div className="mu-user-cell">
            <span className="mu-user-avatar">{initials}</span>
            <div>
              <div className="mu-user-name">{record.name || "—"}</div>
              <div className="mu-user-email">{record.email || "—"}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <span className={roleTagClass(role)}>{ROLE_LABEL_MAP[role] || role}</span>
      ),
    },
    {
      title: "Access",
      key: "access",
      render: (_, record) => (
        <span className="text-muted fs-7">
          {record.accounts_count || 0} accounts · {record.modules_count || 0} modules
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const pillClass =
          status === "pending"
            ? "mu-status-pill--pending"
            : status === "disabled"
              ? "mu-status-pill--disabled"
              : "mu-status-pill--active";

        return (
          <span className={`mu-status-pill ${pillClass}`}>
            {SUB_USER_STATUS_LABELS[status] || "Active"}
          </span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 72,
      render: (_, record) => (
        <Dropdown overlay={actionMenu(record)} trigger={["click"]} disabled={!canEdit}>
          <Button type="link" className="mu-action-btn p-0" disabled={!canEdit}>
            <i className="ki-solid ki-dots-vertical fs-2" />
          </Button>
        </Dropdown>
      ),
    },
  ];

  if (view === "invite") {
    return (
      <Wrapper>
        <Helmet>
          <title>{editingUser ? "Edit Sub-user" : "Invite Sub-user"}</title>
        </Helmet>
        <InviteSubUserForm
          userData={userData}
          editingUser={editingUser}
          onBack={backToList}
          onSuccess={handleInviteSuccess}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Helmet>
        <title>Manage Users</title>
      </Helmet>

     {/* Search input */}
      <div className="mb-10 mx-md-n9 mx-n2 px-10 py-5 bg-white filter">
          <div className="position-relative">
              <i className="ki-outline ki-magnifier fs-2 position-absolute top-50 translate-middle-y ms-4" style={{ color: "var(--app-text-muted)" }} />
              <input type="text" value={searchText} onChange={handleSearch} className={`form-control form-control-solid text-dark border border-gray-300 fs-7 ps-12 all-search manage-users-search-input ${isMobile ? 'w-100' : 'w-300px'}`} placeholder="Search by name or email…" />
          </div>
      </div>

      {/* Team members table */}
      <div className="card">
        <div className="card-header border-bottom-dashed px-6 py-4 d-flex align-items-center justify-content-between">
          <div className="d-flex flex-column"> 
            <h3 className="card-title fw-bolder">Team members</h3>
            <p className="card-subtitle text-muted fs-7">
              Invite users and control which seller accounts and modules they can access.
            </p>
          </div>

            <div className="card-toolbar gap-2 flex-wrap align-items-center">
                  <button className="btn btn-sm btn-danger fs-7 fw-bold" onClick={openInvite} disabled={!canEdit}>
                      <PlusOutlined className="me-1" /> Invite User
                  </button>
              </div>
        </div>
        <div className="card-body py-2 px-2 px-md-4">
          {/* {loading && list.length === 0 ? (
            <div className="text-center py-10">
              <Spin size="large" />
            </div>
          ) : list.length === 0 ? (
            <div className="text-center py-12 px-4">
              <div className="mb-3" style={{ fontSize: 40, opacity: 0.35 }}>
                <PlusOutlined />
              </div>
              <h5 className="fw-bold mb-2">No sub-users yet</h5>
              <p className="text-muted mb-4">
                Invite teammates to collaborate on your seller accounts with role-based access.
              </p>
              <button type="button" className="mu-invite-btn" onClick={openInvite}>
                <UserAddOutlined /> Invite your first sub-user
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table
                scroll={{ x: "max-content" }}
                columns={columns}
                dataSource={list}
                rowKey="id"
                loading={loading}
                pagination={false}
                components={{
                  table: (p) => (
                    <table
                      className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7 mu-page-table"
                      {...p}
                    />
                  ),
                  header: {
                    row: (p) => <tr {...p} className="fw-bolder mu-table-header-row bg-cus" />,
                  },
                }}
              />
            </div>
          )} */}
          <div className="table-responsive">
              <Table
                scroll={{ x: "max-content" }}
                columns={columns}
                dataSource={list}
                rowKey="id"
                loading={loading}
                pagination={false}
                locale={{
                  emptyText: (
                    <Empty
                      description={loading ? "Loading..." : "No team members found."}
                    />
                  ),
                }}
                // locale={{ emptyText: <Empty description="No Products Found" /> }}
                components={{
                  table: (p) => (
                    <table
                      className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7 mu-page-table"
                      {...p}
                    />
                  ),
                  header: {
                    row: (p) => <tr {...p} className="fw-bolder mu-table-header-row bg-cus" />,
                  },
                }}
              />
            </div>
        </div>
      </div>

      {totalPage > 0 ? (
        <div className="card card-xxl-stretch mt-5 mb-5 border-0 shadow-none ">
          <div className="card-body py-2 ">
            <ConfigProvider theme={theme}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={totalPage}
                showSizeChanger
                onChange={(p, s) => {
                  setPage(p);
                  setPageSize(s);
                }}
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} members`}
              />
            </ConfigProvider>
          </div>
        </div>
      ) : null}

      <Modal
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        footer={null}
        centered
        width={420}
        className="mu-delete-modal"
      >
        <div className="text-center px-2 py-4">
          <div className="mb-4" style={{ fontSize: 48, lineHeight: 1 }}>
            🗑️
          </div>
          <h4 className="fw-bolder mu-delete-title mb-2">Remove sub-user?</h4>
          <p className="text-muted mb-6">
            <span className="fw-bold mu-delete-name">{deleteTarget?.name}</span> will lose access
            immediately. This cannot be undone.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button
              size="large"
              className="mu-delete-cancel fs-6"
              onClick={() => setDeleteTarget(null)}
              style={{ minWidth: 110 }}
            >
              Cancel
            </Button>
            <Button
              danger
              type="primary"
              size="large"
              className="fs-6"
              style={{ minWidth: 110 }}
              onClick={() => handleDelete(deleteTarget?.id)}
            >
              Remove
            </Button>
          </div>
        </div>
      </Modal>
    </Wrapper>
  );
}
